import React, { useState, useEffect, useRef } from "react";
import {
  View,
  TextInput,
  Animated,
  StyleSheet,
  Text,
  KeyboardTypeOptions,
  Platform,
  TouchableOpacity,
  ActivityIndicator
} from "react-native";
import Colors from "../../../constants/colors";
import { inputAssistDisabledProps } from "./FloatingTextInputComponent";
import RoundedCloseIcon from "../../../../../../assets/images/RoundedCloseIcon.svg";
import WarningIcon from "../../../../../../assets/images/warning.svg";
import { useTranslation } from "react-i18next";

/** Outer container horizontal inset (Figma space-16). */
const PROMO_CONTAINER_PADDING_H = 10;
const PROMO_FIELD_HEIGHT = 56;
const PROMO_VALUE_LINE_HEIGHT = 24;
const PROMO_LABEL_LINE_HEIGHT = 20;
const PROMO_LABEL_TOP = 6;
/** Space between floated label and value (Figma: tighter than bottom inset). */
const PROMO_LABEL_VALUE_GAP = 2;
const PROMO_VALUE_BOTTOM_INSET = 10;
/** Apply button width + gap before typed text (inset from outer border). */
const PROMO_APPLY_BUTTON_WIDTH = 70;
const PROMO_INPUT_PADDING_RIGHT_APPLY =
  PROMO_CONTAINER_PADDING_H + PROMO_APPLY_BUTTON_WIDTH + 12;
const PROMO_INPUT_PADDING_RIGHT_CLEAR = PROMO_CONTAINER_PADDING_H + 40;
type PromoInputState = "disabled" | "error" | "success" | "default" | "typing" | "filled";

type Props = {
  label?: string;
  value?: string;
  isAutoFocus?: boolean;
  testID?: string;
  onChangeText: (text: string) => void;
  editable?: boolean;
  keyboardType?: KeyboardTypeOptions;
  errorText?: string | null;
  maxLength?: number;
  handleApplyPromoCode?: () => void;
  clearPromoCode?: () => void;
  activityIndicatorStatus?: boolean;
  isPromocodeApplied?: boolean;
  /** Forwarded to the underlying TextInput (e.g. web send review footer layout). */
  onFieldFocus?: () => void;
  onFieldBlur?: () => void;
};

const PromocodeTextInputComponent: React.FC<Props> = ({
  label,
  value,
  isAutoFocus,
  testID,
  onChangeText,
  editable = true,
  keyboardType,
  errorText,
  maxLength,
  handleApplyPromoCode,
  clearPromoCode,
  activityIndicatorStatus,
  isPromocodeApplied,
  onFieldFocus,
  onFieldBlur,
}) => {
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState(false);
  const animated = useRef(new Animated.Value(value ? 1 : 0)).current;

  const hasPromoInput = Boolean((value ?? "").trim().length > 0);
  const hasFloatingLabel = isFocused || hasPromoInput;
  const isPromoSuccess = Boolean(isPromocodeApplied && !errorText);

  const inputState: PromoInputState = !editable
    ? "disabled"
    : isPromoSuccess
      ? "success"
      : errorText
        ? "error"
        : !hasPromoInput
          ? "default"
          : isFocused
            ? "typing"
            : "filled";

  useEffect(() => {
    Animated.timing(animated, {
      toValue: isFocused || !!value ? 1 : 0,
      duration: 180,
      useNativeDriver: false,
    }).start();
  }, [isFocused, value]);

  /** Empty: center the label in 56px. Floated: 6px top inset (pairs with inputFloated padding). */
  const labelStyle = {
    position: "absolute" as const,
    left: PROMO_CONTAINER_PADDING_H,
    top: animated.interpolate({
      inputRange: [0, 1],
      outputRange: [
        (PROMO_FIELD_HEIGHT - PROMO_LABEL_LINE_HEIGHT) / 2,
        PROMO_LABEL_TOP,
      ],
    }),
    fontSize: animated.interpolate({
      inputRange: [0, 1],
      outputRange: [16, 14],
    }),
    lineHeight: PROMO_LABEL_LINE_HEIGHT,
    color: Colors.subTitleColor,
    fontFamily: "Rubik",
  };

  const borderColor =
    inputState === "error"
      ? Colors.errorRedColor
      : inputState === "typing" || (inputState === "filled" && isFocused)
        ? Colors.titleTextColor
        : Colors.textInputBackground;

  const isApplyEnabled =
    editable && hasPromoInput && !activityIndicatorStatus && !isPromoSuccess;

  const applyButtonStyle = [
    styles.promoApplyButton,
    isApplyEnabled
      ? { backgroundColor: Colors.titleTextColor, borderColor: Colors.titleTextColor }
      : { backgroundColor: Colors.borderGrey, borderColor: Colors.applyText },
  ];

  const applyTextStyle = [
    styles.applyText,
    { color: Colors.buttonTextColor },
  ];

  const applyButtonDisabled = !isApplyEnabled;

  const inputTextColor =
    inputState === "success"
      ? Colors.promocodeSuccessGreen
      : inputState === "disabled"
        ? Colors.infoSubValue
        : Colors.titleTextColor;

  return (
    <View style={{ marginVertical: 8 }}>
      <View style={[styles.container, { borderColor}]}>
        <View pointerEvents="none" style={StyleSheet.absoluteFillObject}>
          <Animated.Text style={labelStyle}>{label}</Animated.Text>
        </View>
        <TextInput
          testID={testID}
          autoFocus={isAutoFocus}
          value={value}
          style={[
            styles.input,
            hasFloatingLabel ? styles.inputFloated : styles.inputEmptyCentered,
            {
              color: inputTextColor,
              paddingRight: isPromoSuccess
                ? PROMO_INPUT_PADDING_RIGHT_CLEAR
                : PROMO_INPUT_PADDING_RIGHT_APPLY,
            },
            Platform.OS === "web" && {
              WebkitTextFillColor: inputTextColor,
            },
          ]}
          placeholderTextColor={Colors.subTitleColor}
          onFocus={() => {
            setIsFocused(true);
            onFieldFocus?.();
          }}
          onBlur={() => {
            setIsFocused(false);
            onFieldBlur?.();
          }}
          onChangeText={onChangeText}
          editable={editable}
          keyboardType={keyboardType}
          maxLength={maxLength}
          {...inputAssistDisabledProps}
        />
        {isPromoSuccess ? (
          <TouchableOpacity
            testID="clearButton"
            style={styles.clearButton}
            onPress={clearPromoCode}
          >
            <RoundedCloseIcon width={24} height={24} />
          </TouchableOpacity>
        ) : (
          <View style={styles.promoApplyButtonSlot} pointerEvents="box-none">
            <TouchableOpacity
              style={applyButtonStyle}
              disabled={applyButtonDisabled}
              testID="promoApplyButton"
              onPress={handleApplyPromoCode}
            >
              {activityIndicatorStatus ? (
                <ActivityIndicator
                  size="small"
                  color={Colors.white}
                  style={{ paddingVertical: 4, paddingHorizontal: 6 }}
                />
              ) : (
                <Text style={applyTextStyle}>{t("APPLY")}</Text>
              )}
            </TouchableOpacity>
          </View>
        )}
      </View>
      {
        errorText && (
          <View style={{ flexDirection: 'row', alignItems: 'center', marginTop: 8}}>
            <WarningIcon height={16} width={16}/>
            <Text style={styles.errorText}>{errorText}</Text>
          </View>
        )
      }
    </View>
  );
};

export default PromocodeTextInputComponent;

const styles = StyleSheet.create({
  container: {
    height: PROMO_FIELD_HEIGHT,
    borderWidth: 2,
    borderRadius: 8,
    backgroundColor: Colors.textInputBackground,
    position: "relative",
  },
  input: {
    height: "100%",
    paddingLeft: PROMO_CONTAINER_PADDING_H,
    paddingRight: 0,
    fontSize: 16,
    fontWeight: "400",
    fontFamily: "Rubik",
    lineHeight: PROMO_VALUE_LINE_HEIGHT,
    ...(Platform.OS === "web" ? ({ outlineStyle: "none" } as any) : {}),
  },
  /** Label only (default): vertically centered placeholder. */
  inputEmptyCentered: {
    paddingTop: (PROMO_FIELD_HEIGHT - PROMO_VALUE_LINE_HEIGHT) / 2,
    paddingBottom: (PROMO_FIELD_HEIGHT - PROMO_VALUE_LINE_HEIGHT) / 2,
    ...(Platform.OS === "android"
      ? { textAlignVertical: "center" as const, includeFontPadding: false }
      : {}),
  },
  /** Floated: label (6+20) + 2px gap + value + 10px bottom inset. */
  inputFloated: {
    paddingTop: PROMO_LABEL_TOP + PROMO_LABEL_LINE_HEIGHT + PROMO_LABEL_VALUE_GAP,
    paddingBottom: PROMO_VALUE_BOTTOM_INSET,
    ...(Platform.OS === "android"
      ? { textAlignVertical: "top" as const, includeFontPadding: false }
      : {}),
  },
  errorText: {
    fontSize: 16,
    fontFamily: 'Rubik',
    fontWeight: '400',
    lineHeight: 20,
    color: Colors.errorRedColor,
    marginLeft: 8
  },
  clearButton: {
    position: "absolute",
    right: PROMO_CONTAINER_PADDING_H,
    top: 0,
    bottom: 0,
    width: 40,
    justifyContent: "center",
    alignItems: "flex-end",
  },
  promoApplyButtonSlot: {
    position: "absolute",
    right: PROMO_CONTAINER_PADDING_H,
    top: 0,
    bottom: 0,
    justifyContent: "center",
    alignItems: "center",
  },
  promoApplyButton: {
    borderRadius: 8,
    // borderWidth: 1,
    borderColor: Colors.applyText,
    backgroundColor: Colors.borderGrey,
    minWidth: PROMO_APPLY_BUTTON_WIDTH,
    height: 32,
    paddingHorizontal: 12,
    alignItems: "center",
    justifyContent: "center",
  },
  applyText: {
    fontFamily: "Rubik",
    fontSize: 14,
    fontWeight: "500",
    color: Colors.buttonTextColor,
    lineHeight: 16,
  },
});